import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { round } from 'lodash-es';
import { useState } from 'react';
import { toast } from 'react-toastify';

type Entry = { quantity: number; timestamp?: string; actor?: any; __typename?: string };

interface ActualCookedEntriesModalProps {
  open: boolean;
  onClose: () => void;
  entries: Entry[];
  canEdit: boolean;
  onEdit: (index: number, newQuantity: number) => Promise<void>;
  onDelete: (index: number) => Promise<void>;
  isUpdating?: boolean;
}

const ActualCookedEntriesModal = ({
  open,
  onClose,
  entries,
  canEdit,
  onEdit,
  onDelete,
  isUpdating = false
}: ActualCookedEntriesModalProps) => {
  const theme = useTheme();
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  const confirmEntryEdit = async () => {
    if (editIndex === null) return;
    const q = Number(editValue);
    if (!Number.isFinite(q) || q <= 0) {
      toast.error('Enter a valid positive number');
      return;
    }
    try {
      await onEdit(editIndex, q);
      setEditIndex(null);
      setEditValue('');
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to update entry');
    }
  };

  const resetEntryEdit = () => {
    setEditIndex(null);
    setEditValue('');
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        resetEntryEdit();
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Actual cooked entries</DialogTitle>
      <DialogContent>
        {isUpdating && (
          <div style={{ marginBottom: 8, width: '100%' }}>
            <div style={{ height: 4, backgroundColor: (theme as any).palette.neutral200 }}>
              <div
                style={{
                  height: 4,
                  width: '100%',
                  backgroundColor: (theme as any).palette.primary500,
                  animation: 'pulse 1.2s infinite'
                }}
              />
            </div>
          </div>
        )}
        <List>
          {(entries ?? []).map((entry, index) => {
            const quantity = Number(entry?.quantity) || 0;
            const timestamp = entry?.timestamp ? new Date(entry.timestamp).toLocaleString() : '';
            const entryActor = typeof entry?.actor === 'string' ? entry.actor : (entry?.actor?.name ?? '');
            const isEditingThis = editIndex === index;
            return (
              <ListItem key={`acw-${index}`} divider>
                {isEditingThis ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    <TextField
                      size="small"
                      type="number"
                      autoFocus
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isUpdating) {
                          e.preventDefault();
                          void confirmEntryEdit();
                        } else if (e.key === 'Escape') {
                          e.stopPropagation();
                          resetEntryEdit();
                        }
                      }}
                      sx={{ width: 120 }}
                      disabled={isUpdating}
                      slotProps={{
                        htmlInput: { min: 0, step: '0.01' }
                      }}
                    />
                    <Typography sx={{ fontSize: '14px', color: theme.palette.neutral600, flexGrow: 1 }}>
                      {timestamp} • {entryActor}
                    </Typography>
                    <IconButton disabled={isUpdating} onClick={() => void confirmEntryEdit()}>
                      <CheckIcon sx={{ fontSize: '22px' }} />
                    </IconButton>
                    <IconButton disabled={isUpdating} onClick={resetEntryEdit}>
                      <CloseIcon sx={{ fontSize: '22px' }} />
                    </IconButton>
                  </Stack>
                ) : (
                  <>
                    <ListItemText
                      primary={`${round(quantity, 2)}g`}
                      secondary={`${timestamp} • ${entryActor}`}
                      slotProps={{
                        primary: { sx: { fontWeight: 600 } },
                        secondary: { sx: { fontSize: '13px' } }
                      }}
                    />
                    <ListItemSecondaryAction>
                      {canEdit && (
                        <>
                          <IconButton
                            disabled={isUpdating}
                            onClick={() => {
                              setEditIndex(index);
                              setEditValue(String(quantity));
                            }}
                          >
                            <EditIcon sx={{ fontSize: '22px' }} />
                          </IconButton>
                          <IconButton
                            disabled={isUpdating}
                            onClick={() => {
                              void (async () => {
                                try {
                                  await onDelete(index);
                                } catch (error: any) {
                                  toast.error(error?.message ?? 'Failed to delete entry');
                                }
                              })();
                            }}
                          >
                            <CloseIcon sx={{ fontSize: '22px' }} />
                          </IconButton>
                        </>
                      )}
                    </ListItemSecondaryAction>
                  </>
                )}
              </ListItem>
            );
          })}
          {(entries ?? []).length === 0 && (
            <Typography sx={{ px: 1, py: 1, color: theme.palette.neutral600 }}>No entries yet</Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            resetEntryEdit();
            onClose();
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActualCookedEntriesModal;
